import { Box, Checkbox, HStack, Tr } from '@chakra-ui/react';
import { useContext, useMemo, useState } from 'react';
import { TbLayoutGrid, TbShieldLock } from 'react-icons/tb';
import SessionContext from '../../../../../contexts/SessionContext';
import type { UserPermission } from '../../../../../hooks/useEditDefaultPermissions';
import type { Permission, WebsiteTab } from '../../../../../hooks/useValidateSession';
import {
    SettingsSubTable,
    SettingsTableTabs,
    SettingsTd,
    SettingsTh,
    useSubTablePagination,
} from '../../../../../components/settings/primitives';
import {
    PERMISSION_LABELS,
    TAB_LABELS,
    toggleValue,
    visibleMatrixRows,
} from '../../permissions/permissionCatalog';

type MatrixView = 'tabs' | 'permissions';

export interface InvitePermissionsEditorProps {
    /** The set being edited: a custom override, or a copy of the defaults. */
    value: UserPermission;
    onChange: (next: UserPermission) => void;
    /** Force every row on and lock the editor (an admin has everything). */
    isAllChecked?: boolean;
    isDisabled?: boolean;
    /**
     * Rows to offer instead of the ones this org has turned on, for access that
     * applies in every organization (Yaxle employees).
     */
    matrixRows?: { tabs: Array<WebsiteTab>; permissions: Array<Permission> };
}

/**
 * Tabs-and-permissions matrix for users who do not exist yet: the same two-view
 * checkbox list as the user detail page, but writing into an invite draft
 * instead of a saved user. Feature-gated rows are hidden here exactly as they
 * are in the defaults matrix, so what an admin sees is what the org has.
 */
const InvitePermissionsEditor = ({
    value,
    onChange,
    isAllChecked = false,
    isDisabled = false,
    matrixRows,
}: InvitePermissionsEditorProps) => {
    const session = useContext(SessionContext);
    const [view, setView] = useState<MatrixView>('tabs');

    const { tabs: visibleTabs, permissions: visiblePermissions } = useMemo(
        () =>
            matrixRows ??
            visibleMatrixRows({
                invoicingEnabled: session?.invoicingEnabled === true,
                inventoryEnabled: session?.inventoryEnabled === true,
            }),
        [matrixRows, session?.invoicingEnabled, session?.inventoryEnabled],
    );

    const isTabsView = view === 'tabs';
    const rows: Array<Permission | WebsiteTab> = isTabsView ? visibleTabs : visiblePermissions;
    const labels: Record<string, string> = isTabsView ? TAB_LABELS : PERMISSION_LABELS;

    const { currentPage, setCurrentPage, totalPages, pageItems } = useSubTablePagination(rows);

    const isChecked = (item: Permission | WebsiteTab) => {
        if (isAllChecked) return true;
        return isTabsView
            ? value.tabs.includes(item as WebsiteTab)
            : value.permissions.includes(item as Permission);
    };

    const handleToggle = (item: Permission | WebsiteTab) => {
        onChange(
            isTabsView
                ? { ...value, tabs: toggleValue(value.tabs, item as WebsiteTab) }
                : { ...value, permissions: toggleValue(value.permissions, item as Permission) },
        );
    };

    // Header check-all covers the rows in view only, so feature-gated values
    // hidden from this org are never rewritten.
    const handleSetAll = (enabled: boolean) => {
        if (isTabsView) {
            const kept = value.tabs.filter((tab) => !visibleTabs.includes(tab));
            onChange({ ...value, tabs: enabled ? [...kept, ...visibleTabs] : kept });
            return;
        }
        const kept = value.permissions.filter(
            (permission) => !visiblePermissions.includes(permission),
        );
        onChange({
            ...value,
            permissions: enabled ? [...kept, ...visiblePermissions] : kept,
        });
    };

    const allChecked = rows.length > 0 && rows.every((item) => isChecked(item));
    const someChecked = rows.some((item) => isChecked(item));

    return (
        <>
            <SettingsTableTabs
                tabs={[
                    {
                        id: 'tabs',
                        label: 'Tabs',
                        icon: TbLayoutGrid as React.ElementType,
                        count: visibleTabs.length,
                    },
                    {
                        id: 'permissions',
                        label: 'Permissions',
                        icon: TbShieldLock as React.ElementType,
                        count: visiblePermissions.length,
                    },
                ]}
                value={view}
                onChange={(id) => setView(id as MatrixView)}
                px={0}
            />

            <SettingsSubTable
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                head={
                    <>
                        <SettingsTh minW="220px">{isTabsView ? 'Tab' : 'Permission'}</SettingsTh>
                        <SettingsTh>
                            <HStack spacing={1.5}>
                                <Checkbox
                                    size="sm"
                                    colorScheme="partbay-blue"
                                    isChecked={allChecked}
                                    isIndeterminate={!allChecked && someChecked}
                                    isDisabled={isDisabled || isAllChecked}
                                    onChange={(e) => handleSetAll(e.target.checked)}
                                    aria-label={`Toggle all ${isTabsView ? 'tabs' : 'permissions'}`}
                                />
                                <Box as="span">Enabled</Box>
                            </HStack>
                        </SettingsTh>
                    </>
                }
            >
                {pageItems.map((item) => (
                    <Tr key={item} _hover={{ bg: 'gray.50' }}>
                        <SettingsTd fontWeight="medium" color="gray.800" whiteSpace="nowrap">
                            {labels[item] ?? item}
                        </SettingsTd>
                        <SettingsTd>
                            <Checkbox
                                colorScheme="partbay-blue"
                                isChecked={isChecked(item)}
                                isDisabled={isDisabled || isAllChecked}
                                onChange={() => handleToggle(item)}
                                aria-label={labels[item] ?? item}
                            />
                        </SettingsTd>
                    </Tr>
                ))}
            </SettingsSubTable>
        </>
    );
};

export default InvitePermissionsEditor;
